import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { Warning } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';

// --- Global In-Memory Cache Setup (from Image component) ---
// Ensure this runs only once
if (typeof window !== 'undefined' && !window.imageCache) {
    console.log("Initializing global in-memory image cache");
    window.imageCache = new Map();
    window.imageCacheMeta = {
        size: 0,
        maxSize: 150, // Max number of items
        storage: 0,
        maxStorage: 100 * 1024 * 1024, // Max storage in bytes (e.g., 100MB)
        lastUsed: new Map(), // Map<src, timestamp>
        blobSizes: new Map(), // Map<src, sizeInBytes>
        manageCallback: () => {
            const meta = window.imageCacheMeta;
            if (meta.size > meta.maxSize || meta.storage > meta.maxStorage) {
                console.log(`Cache limit exceeded (Items: ${meta.size}/${meta.maxSize}, Size: ${(meta.storage / 1024 / 1024).toFixed(2)}MB/${(meta.maxStorage / 1024 / 1024).toFixed(2)}MB). Evicting oldest entries...`);
                while (meta.size > meta.maxSize || meta.storage > meta.maxStorage) {
                    let oldestSrc = null;
                    let oldestTime = Date.now();
                    for (let [key, value] of meta.lastUsed) {
                        if (value < oldestTime) {
                            oldestSrc = key;
                            oldestTime = value;
                        }
                    }

                    if (!oldestSrc) break; // Should not happen if size > 0

                    const blobUrl = window.imageCache.get(oldestSrc);
                    const blobSize = meta.blobSizes.get(oldestSrc) || 0;

                    window.imageCache.delete(oldestSrc);
                    meta.lastUsed.delete(oldestSrc);
                    meta.blobSizes.delete(oldestSrc);

                    if (blobUrl) {
                        // Important: Revoke the Object URL to free memory
                        URL.revokeObjectURL(blobUrl);
                    }

                    meta.size = window.imageCache.size;
                    meta.storage -= blobSize;

                    console.log(`Evicted from in-memory cache: ${oldestSrc}. New size: ${meta.size}, New storage: ${(meta.storage / 1024 / 1024).toFixed(2)}MB`);
                }
            }
        },
        clear: () => {
            console.log("Clearing global in-memory image cache");
            for (let blobUrl of window.imageCache.values()) {
                URL.revokeObjectURL(blobUrl);
            }
            window.imageCache = new Map();
            window.imageCacheMeta.size = 0;
            window.imageCacheMeta.storage = 0;
            window.imageCacheMeta.lastUsed = new Map();
            window.imageCacheMeta.blobSizes = new Map();
        },
        addItem: (src, objectURL, blobSize) => {
            // Revoke previous URL if replacing
            if (window.imageCache.has(src)) {
                 const oldUrl = window.imageCache.get(src);
                 const oldSize = window.imageCacheMeta.blobSizes.get(src) || 0;
                 URL.revokeObjectURL(oldUrl);
                 window.imageCacheMeta.storage -= oldSize;
                 window.imageCacheMeta.blobSizes.delete(src);
            }

            window.imageCache.set(src, objectURL);
            window.imageCacheMeta.lastUsed.set(src, Date.now());
            window.imageCacheMeta.blobSizes.set(src, blobSize);
            window.imageCacheMeta.size = window.imageCache.size;
            window.imageCacheMeta.storage += blobSize;
            window.imageCacheMeta.manageCallback(); // Check if eviction needed
        },
        updateLastUsed: (src) => {
             if (window.imageCache.has(src)) {
                 window.imageCacheMeta.lastUsed.set(src, Date.now());
             }
        }
    };

    // Optional: Add a way to configure cache sizes
    window.setInMemoryCacheLimits = (maxItems, maxBytes) => {
        if (typeof maxItems === 'number' && maxItems > 0) {
            window.imageCacheMeta.maxSize = maxItems;
        }
        if (typeof maxBytes === 'number' && maxBytes > 0) {
             window.imageCacheMeta.maxStorage = maxBytes;
        }
        console.log(`In-memory cache limits updated: Items=${window.imageCacheMeta.maxSize}, Storage=${(window.imageCacheMeta.maxStorage / 1024 / 1024).toFixed(2)}MB`);
        window.imageCacheMeta.manageCallback(); // Apply new limits immediately
    };
}
// --- End Global Cache Setup ---

const DISK_CACHE_NAME = 'media-cache'; // Use a more generic name

function getGlobalDisableDiskCache() {
    if (typeof window === 'undefined') return false;
    if (typeof window.disableDiskCache === 'boolean') return window.disableDiskCache;
    return localStorage.getItem('ifmwDisableDiskCache') === 'true';
}

if (typeof window !== 'undefined' && !window.clearLocalMediaCache) {
    window.clearLocalMediaCache = async () => {
        try {
            if (window.imageCacheMeta?.clear) {
                window.imageCacheMeta.clear();
            }

            if ('caches' in window) {
                await caches.delete(DISK_CACHE_NAME);
            }
        } catch (e) {
            console.error('Failed to clear local media cache:', e);
        }
    };
}

// Define common video extensions/mimetypes
const VIDEO_EXTENSIONS = ["mp4", "webm", "ogv", "mov"]; // Add more if needed
const VIDEO_MIMETYPES_PREFIX = ["video/"];

const CachedMedia = forwardRef((props, ref) => {
    const {
        src,                // Source URL (required)
        item,               // Optional item object with 'filename' or 'mimetype' for video detection hint
        eCallback,          // Optional error callback
        videoProps = {},    // Props specific to the <video> tag (e.g., { autoPlay: true, loop: true, muted: true })
        style,              // Common styles
        alt = "",           // Alt text for images
        disableMemCache = false, // Option to bypass in-memory cache
        disableDiskCache,        // Option to bypass disk cache
        ...other            // Rest props for <img> or <video>
    } = props;

    const effectiveDisableDiskCache = disableDiskCache ?? getGlobalDisableDiskCache();

    const [error, setError] = useState(null);
    const [dsrc, setDsrc] = useState(null); // The Blob URL to be used in <img> or <video>
    const [loading, setLoading] = useState(true);
    const [isVideo, setIsVideo] = useState(false);
    const fetchControllerRef = useRef(null);
    const cacheRef = useRef(null); // For the Cache API instance
    const isMounted = useRef(true); // Track mount status

    // --- Effect for revoking previous Object URL ---
    // This runs *after* the main effect sets a *new* dsrc, cleaning up the *old* one.
    useEffect(() => {
        isMounted.current = true;
        let currentDsrc = dsrc; // Capture dsrc at the time the effect runs

        return () => {
             isMounted.current = false;
            // Revoke the Object URL when the component unmounts or dsrc changes
            if (currentDsrc && currentDsrc.startsWith('blob:')) {
                // Only revoke if it's still in memory cache *or* if we are unmounting
                // If it was evicted, it's already revoked.
                // Let the in-memory cache handle revocation on eviction.
                // We only revoke here on *unmount* or *src change* leading to a *new* dsrc.
                // Check if the URL still exists in the main cache to avoid revoking prematurely if evicted
                // This check might be complex if eviction happens between render and cleanup.
                // Simplification: Always revoke on unmount/dsrc change. The cache eviction will also try, which is safe (revoking twice is ok).
                URL.revokeObjectURL(currentDsrc);
            }
        };
    }, [dsrc]); // Depend only on dsrc

    // --- Main Loading Effect ---
    useEffect(() => {
        if (!src) {
            setLoading(false);
            setError(null);
            setDsrc(null);
            setIsVideo(false);
            return;
        }

        setLoading(true);
        setError(null);
        // Don't reset dsrc here, let the cleanup handle the old one

        // Abort any previous ongoing fetch
        if (fetchControllerRef.current) {
            fetchControllerRef.current.abort();
        }
        fetchControllerRef.current = new AbortController();
        const signal = fetchControllerRef.current.signal;

        const detectVideo = (sourceUrl, itemHint, contentType) => {
            if (itemHint) {
                const filename = itemHint.filename || "";
                const mimetype = itemHint.mimetype || "";
                 // Extract extension robustly, handling potential query params
                const extension = filename.split(/[?#]/)[0].split('.').pop()?.toLowerCase();

                if ((extension && VIDEO_EXTENSIONS.includes(extension)) || VIDEO_MIMETYPES_PREFIX.some(prefix => mimetype.startsWith(prefix))) {
                    return true;
                }
            }
             // Fallback: Check Content-Type header if available
            if (contentType && VIDEO_MIMETYPES_PREFIX.some(prefix => contentType.startsWith(prefix))) {
                 return true;
            }
             // Fallback: Check URL extension if no other info
             const urlExtension = sourceUrl.split(/[?#]/)[0].split('.').pop()?.toLowerCase();
             if (urlExtension && VIDEO_EXTENSIONS.includes(urlExtension)) {
                return true;
             }

            return false;
        };


        const loadImage = async () => {
            try {
                // 1. Check In-Memory Cache
                if (!disableMemCache && window.imageCache?.has(src)) {
                    const cachedUrl = window.imageCache.get(src);
                    window.imageCacheMeta.updateLastUsed(src);
                    const detectedVideo = detectVideo(src, item); // Re-detect based on item/src
                    if (isMounted.current) {
                        setIsVideo(detectedVideo);
                        setDsrc(cachedUrl);
                        setLoading(false);
                    }
                    return;
                }

                // 2. Check Disk Cache (Cache API)
                if (!effectiveDisableDiskCache) {
                    if (!cacheRef.current) {
                        cacheRef.current = await caches.open(DISK_CACHE_NAME);
                    }
                    const cache = cacheRef.current;
                    let response = await cache.match(src);

                    if (response) {
                        console.log("Using disk cache:", src);
                        const blob = await response.blob();
                        const objectURL = URL.createObjectURL(blob);
                        const detectedVideo = detectVideo(src, item, response.headers.get('Content-Type'));

                         // Add to in-memory cache
                        if (!disableMemCache && window.imageCacheMeta) {
                            window.imageCacheMeta.addItem(src, objectURL, blob.size);
                        }

                        if (isMounted.current) {
                            setIsVideo(detectedVideo);
                            setDsrc(objectURL);
                            setLoading(false);
                        }
                         // If mem cache is disabled, we need to revoke this URL ourselves later
                         // The dsrc useEffect cleanup handles this
                         if (disableMemCache) {
                             console.warn("In-memory cache disabled, Object URL will be revoked on unmount/change.");
                         }
                        return;
                    }
                }

                // 3. Fetch from Network
                setDsrc(null); // Ensure loading spinner shows

                const networkResponse = await fetch(src, { signal });

                if (!networkResponse.ok) {
                    throw new Error(`Network response was not ok: ${networkResponse.status} ${networkResponse.statusText}`);
                }

                // Clone response for potential caching
                const responseForCache = networkResponse.clone();
                const blob = await networkResponse.blob(); // Get blob for object URL

                 // Check for abort again after await
                if (signal.aborted) {
                     console.log("Fetch aborted after blob download:", src);
                     return;
                 }

                const objectURL = URL.createObjectURL(blob);
                const contentType = networkResponse.headers.get('Content-Type');
                const detectedVideo = detectVideo(src, item, contentType);

                // Add to Disk Cache first (if enabled)
                if (!effectiveDisableDiskCache && cacheRef.current) {
                    // Use responseForCache which hasn't had its body read
                    cacheRef.current.put(src, responseForCache).catch(cacheError => {
                        console.error("Error adding to disk cache:", cacheError);
                    });
                }

                // Add to In-Memory Cache (if enabled)
                if (!disableMemCache && window.imageCacheMeta) {
                    window.imageCacheMeta.addItem(src, objectURL, blob.size);
                }

                if (isMounted.current) {
                    setIsVideo(detectedVideo);
                    setDsrc(objectURL);
                    setLoading(false);
                }
                 // If mem cache is disabled, we need to revoke this URL ourselves later
                 // The dsrc useEffect cleanup handles this
                 if (disableMemCache) {
                     console.warn("In-memory cache disabled, Object URL will be revoked on unmount/change.");
                 }


            } catch (err) {
                if (err.name === 'AbortError') {
                    // Don't set error state if aborted
                    return;
                }

                console.error("Error loading media:", src, err);
                if (isMounted.current) {
                    setError(err.message || 'Failed to load media');
                    setLoading(false);
                    setDsrc(null); // Ensure no broken image/video shows
                    if (eCallback) {
                        eCallback(err);
                    }
                }
            }
        };

        loadImage();

        // Main effect cleanup: Abort fetch controller
        return () => {
            if (fetchControllerRef.current) {
                fetchControllerRef.current.abort();
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [src, item, eCallback, disableMemCache, effectiveDisableDiskCache]); // Re-run when src, item, or cache options change

    // --- Rendering Logic ---

    if (!src) {
        return null; // Render nothing if no src
    }

    if (loading) {
        return (
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 40, // Ensure container takes space
                    height: 40,
                    ...style // Apply style to loader container too
                }}
            >
                <CircularProgress size={40} color="primary" />
            </div>
        );
    }

    if (error) {
        return (
            <div
                title={`Error loading ${src}: ${error}`} // Tooltip for error details
                style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                    color: 'rgba(255, 0, 0, 0.7)', // Adjust color/opacity
                    backgroundColor: 'rgba(0, 0, 0, 0.05)', // Subtle background
                    ...style, // Apply style to error container
                }}
            >
                <Warning
                    fontSize="inherit" // Inherit size from container style if set
                    sx={{ fontSize: style?.height || style?.width || 40 }} // Fallback size
                />
            </div>
        );
    }

    if (dsrc) {
        if (isVideo) {
            return (
                <video
                    ref={ref}
                    style={style}
                    src={dsrc}
                    // Sensible defaults, can be overridden by videoProps or ...other
                    autoPlay={videoProps.autoPlay ?? true}
                    loop={videoProps.loop ?? true}
                    muted={videoProps.muted ?? true}
                    controls={videoProps.controls ?? false} // Default controls off for previews
                    playsInline // Good practice for mobile
                    {...videoProps} // Apply specific video props
                    {...other}     // Apply remaining props
                    onError={(e) => { // Catch potential video playback errors
                        console.error("Video playback error:", e);
                        if(isMounted.current) setError('Video playback error');
                        if (eCallback) eCallback(e);
                    }}
                />
            );
        } else {
            return (
                <img
                    ref={ref}
                    style={style}
                    src={dsrc}
                    alt={alt}
                    {...other}
                    onError={(e) => { // Catch potential img rendering errors (e.g., invalid blob)
                        console.error("Image rendering error:", e);
                         if(isMounted.current) setError('Image rendering error');
                         // Attempt to clear caches for this broken src
                         if (!disableMemCache && window.imageCache?.has(src)) {
                             const url = window.imageCache.get(src);
                             const size = window.imageCacheMeta.blobSizes.get(src) || 0;
                             window.imageCache.delete(src);
                             window.imageCacheMeta.lastUsed.delete(src);
                             window.imageCacheMeta.blobSizes.delete(src);
                             window.imageCacheMeta.size = window.imageCache.size;
                             window.imageCacheMeta.storage -= size;
                             URL.revokeObjectURL(url); // Clean up broken blob URL
                             console.log("Removed broken image from in-memory cache:", src);
                         }
                         if (!effectiveDisableDiskCache && cacheRef.current) {
                            cacheRef.current.delete(src).then(deleted => {
                                if(deleted) console.log("Removed broken image from disk cache:", src);
                            });
                         }
                        if (eCallback) eCallback(e);
                    }}
                />
            );
        }
    }

    // Should ideally not be reached if logic is correct, but acts as a fallback
    return null;
});

export default CachedMedia;